import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

interface Article {
  id: number;
  title: string;
  sport_type: string;
  league: string;
  source: string;
  content: string;
  link: string;
  views: number;
  created_at: string;
}

interface ArticleComment {
  id: number;
  article_id: number;
  author_id: string;
  content: string;
  created_at: string;
  profiles: { nickname: string } | null;
}

const sportLabelMap: Record<string, string> = {
  soccer: "축구",
  baseball: "야구",
  basketball: "농구",
  volleyball: "배구",
  hockey: "하키",
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export default function SportsArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [article, setArticle] = useState<Article | null>(null);
  const [comments, setComments] = useState<ArticleComment[]>([]);
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);

  const articleId = Number(id);

  const fetchArticle = useCallback(async () => {
    if (!articleId || isNaN(articleId)) {
      setError("존재하지 않는 기사입니다");
      setLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from("sports_articles")
        .select("id, title, sport_type, league, source, content, link, views, created_at")
        .eq("id", articleId)
        .single();

      if (fetchError || !data) {
        setError("기사를 찾을 수 없습니다");
        setLoading(false);
        return;
      }

      setArticle(data);

      // Increment view count
      supabase
        .from("sports_articles")
        .update({ views: (data.views || 0) + 1 })
        .eq("id", articleId)
        .then(() => {});
    } catch {
      setError("기사를 불러오는 중 오류가 발생했습니다");
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  const fetchComments = useCallback(async () => {
    try {
      const { data, error: commentsError } = await supabase
        .from("article_comments")
        .select("*, profiles(nickname)")
        .eq("article_id", articleId)
        .order("created_at", { ascending: true });

      if (!commentsError && data) {
        setComments(data as ArticleComment[]);
      }
    } catch {
      // Silently fail
    }
  }, [articleId]);

  useEffect(() => {
    fetchArticle();
    fetchComments();
  }, [fetchArticle, fetchComments]);

  const fetchLikeStatus = useCallback(async () => {
    if (!user || !articleId) return;
    try {
      const { data, error: likeError } = await supabase
        .from("article_likes")
        .select("id")
        .eq("article_id", articleId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (!likeError) {
        setLiked(!!data);
      }
    } catch {
      // Silently fail
    }
  }, [user, articleId]);

  const fetchLikeCount = useCallback(async () => {
    if (!articleId) return;
    try {
      const { count, error: countError } = await supabase
        .from("article_likes")
        .select("*", { count: "exact", head: true })
        .eq("article_id", articleId);

      if (!countError && count !== null) {
        setLikeCount(count);
      }
    } catch {
      // Silently fail
    }
  }, [articleId]);

  useEffect(() => {
    fetchLikeStatus();
    fetchLikeCount();
  }, [fetchLikeStatus, fetchLikeCount]);

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!commentText.trim()) return;
    if (commentText.length > 500) return;

    setCommentLoading(true);

    try {
      const { error: insertError } = await supabase.from("article_comments").insert({
        article_id: articleId,
        author_id: user.id,
        content: commentText.trim(),
      });

      if (insertError) return;

      setCommentText("");
      await fetchComments();
    } catch {
      // Silently fail
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    setDeletingCommentId(commentId);

    try {
      const { error: deleteError } = await supabase
        .from("article_comments")
        .delete()
        .eq("id", commentId);

      if (deleteError) return;

      await fetchComments();
    } catch {
      // Silently fail
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleToggleLike = async () => {
    if (!user) return;
    if (likeLoading) return;

    setLikeLoading(true);

    try {
      if (liked) {
        const { error: deleteError } = await supabase
          .from("article_likes")
          .delete()
          .eq("article_id", articleId)
          .eq("user_id", user.id);

        if (deleteError) return;

        setLiked(false);
        setLikeCount(Math.max(0, likeCount - 1));
      } else {
        const { error: insertError } = await supabase
          .from("article_likes")
          .insert({
            article_id: articleId,
            user_id: user.id,
          });

        if (insertError) return;

        setLiked(true);
        setLikeCount(likeCount + 1);
      }
    } catch {
      // Silently fail
    } finally {
      setLikeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="max-w-[800px] mx-auto px-3 md:px-4 py-10 flex items-center justify-center">
          <div className="flex items-center gap-2 text-foreground-600">
            <span className="w-4 h-4 flex items-center justify-center animate-spin">
              <i className="ri-loader-4-line" />
            </span>
            <span className="text-sm">불러오는 중...</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="max-w-[800px] mx-auto px-3 md:px-4 py-10">
          <div className="text-center py-10">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
                <i className="ri-error-warning-line text-2xl" />
              </span>
            </div>
            <h2 className="text-lg font-bold text-foreground-500 mb-2">{error || "기사가 존재하지 않습니다"}</h2>
            <p className="text-sm text-foreground-600 mb-4">삭제되었거나 잘못된 경로입니다</p>
            <Link
              to="/sports"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              스포츠정보로 돌아가기
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const sportLabel = sportLabelMap[article.sport_type] || article.sport_type;

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <div className="max-w-[800px] mx-auto px-3 md:px-4 py-6">
        {/* Back button */}
        <div className="mb-6">
          <Link
            to="/sports"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-600 transition-colors"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            스포츠정보 목록
          </Link>
        </div>

        {/* Article Content */}
        <article className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
          {/* Header */}
          <div className="px-5 md:px-6 py-5 border-b border-background-300">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-0.5 text-[10px] font-bold bg-accent-500/15 text-accent-400 rounded whitespace-nowrap">
                {sportLabel}
              </span>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-700 mb-3 leading-snug">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                <span>{formatDate(article.created_at)}</span>
              </div>
              <span className="text-foreground-500">|</span>
              <div className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line" />
                </span>
                <span>조회 {article.views + 1}</span>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 md:px-6 py-6">
            {article.content ? (
              <div
                className="text-sm text-foreground-600 leading-relaxed prose-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            ) : (
              <div className="text-sm text-foreground-600 leading-relaxed">
                <p>
                  {sportLabel} 종목의 최신 분석 데이터를 바탕으로 한 심층 리포트입니다.
                  주요 경기 데이터, 팀 전력 분석, 선수별 퍼포먼스 지표, 승부예측 포인트 등
                  다양한 관점에서 경기를 분석한 내용을 제공합니다.
                </p>
                <p className="mt-4">
                  전문 분석가들의 의견과 통계 데이터를 종합하여 신뢰도 높은 정보를 전달하며,
                  실시간 업데이트를 통해 최신 트렌드를 반영하고 있습니다.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="px-5 md:px-6 py-4 border-t border-background-300 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button
                onClick={handleToggleLike}
                disabled={likeLoading}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  liked
                    ? "text-primary-400 bg-primary-500/10"
                    : "text-foreground-600 hover:text-primary-400 hover:bg-primary-500/10"
                }`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
                </span>
                <span>좋아요 {likeCount}</span>
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-foreground-600 hover:text-foreground-600 hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-chat-3-line" />
                </span>
                <span>댓글 {comments.length}</span>
              </button>
            </div>
          </div>
        </article>

        {/* Comments Section */}
        <section className="mt-6 bg-background-100 rounded-xl border border-background-300 overflow-hidden">
          <div className="px-5 md:px-6 py-4 border-b border-background-300">
            <h2 className="text-sm font-bold text-foreground-500 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-primary-400">
                <i className="ri-chat-3-line" />
              </span>
              댓글 <span className="text-primary-400">{comments.length}</span>
            </h2>
          </div>

          <div className="divide-y divide-background-300">
            {comments.length === 0 ? (
              <div className="px-5 md:px-6 py-10 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
                  <span className="w-6 h-6 flex items-center justify-center text-foreground-600">
                    <i className="ri-chat-off-line" />
                  </span>
                </div>
                <p className="text-sm text-foreground-600">아직 댓글이 없습니다</p>
                <p className="text-xs text-foreground-600 mt-1">첫 댓글을 작성해보세요</p>
              </div>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="px-5 md:px-6 py-4 group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="w-8 h-8 rounded-full bg-background-200 border border-background-300 flex items-center justify-center flex-shrink-0">
                        <span className="w-4 h-4 flex items-center justify-center text-foreground-600">
                          <i className="ri-user-line text-sm" />
                        </span>
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-foreground-600">
                            {comment.profiles?.nickname || "알 수 없음"}
                          </span>
                          <span className="text-[10px] text-foreground-600">
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed break-words">
                          {comment.content}
                        </p>
                      </div>
                    </div>

                    {user?.id === comment.author_id && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        disabled={deletingCommentId === comment.id}
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:text-red-400 hover:bg-red-500/10 transition-all cursor-pointer"
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-close-line" />
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Comment Input */}
          <div className="px-5 md:px-6 py-4 border-t border-background-300">
            {user ? (
              <form onSubmit={handleAddComment} className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-full bg-background-200 border border-background-300 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-400">
                    <i className="ri-user-fill text-sm" />
                  </span>
                </span>
                <div className="flex-1 min-w-0">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="댓글을 입력하세요"
                    rows={2}
                    maxLength={500}
                    className="w-full bg-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-500 placeholder-foreground-500 border border-background-300 focus:border-primary-500/50 outline-none transition-colors resize-none"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-foreground-600">{commentText.length}/500</span>
                    <button
                      type="submit"
                      disabled={!commentText.trim() || commentLoading}
                      className="px-4 py-1.5 bg-primary-500 text-foreground-50 text-xs font-bold rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      {commentLoading ? "등록 중..." : "댓글 작성"}
                    </button>
                  </div>
                </div>

                {/* honeypot */}
                <div className="hp-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </div>
              </form>
            ) : (
              <div className="text-center py-3">
                <p className="text-sm text-foreground-600">
                  댓글을 작성하려면{" "}
                  <Link to="/login" className="text-primary-400 hover:text-primary-300 font-bold">
                    로그인
                  </Link>
                  이 필요합니다
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}